import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaPlus,
  FaSearch,
  FaTh,
  FaList,
  FaFilter,
  FaQrcode,
  FaEdit,
  FaTrash,
  FaBoxes,
  FaArchive,
  FaBoxOpen,
  FaBuilding,
  FaTags,
  FaChevronDown,
} from 'react-icons/fa';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import AddToPrintQueueButton from '../components/qr/AddToPrintQueueButton';
import Badge from '../components/common/Badge';
import Card from '../components/common/Card';
import Spinner from '../components/common/Spinner';
import EmptyState from '../components/common/EmptyState';
import NextStepNudge from '../components/common/NextStepNudge';
import { Asset, AssetStatus, AssetType } from '../types';
import { toast } from '../utils/toast';
import {
  useInfiniteAssets,
  useAssetTypes,
  useDeleteAsset,
  useGenerateAssetQR,
  useArchiveAsset,
  useUnarchiveAsset,
  usePrefetchAsset,
} from '../dry/hooks';
import { useSites } from '../dry/hooks';
import { useAuth } from '../contexts/AuthContext';
import { isAdmin, isManager } from '../utils/permissions';
import { dedupeById } from '../utils/dedupeById';
import { useDebounce } from '../hooks/useDebounce';
import { useLabel } from '../i18n/useLabel';

const Assets: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const labelSingular = useLabel('asset');
  const labelPlural = useLabel('asset', { form: 'plural' });
  const labelLower = useLabel('asset', { case: 'lower' });
  const labelLowerPlural = useLabel('asset', { form: 'plural', case: 'lower' });
  const labelTypePlural = useLabel('asset_type', { form: 'plural' });
  const labelSitePlural = useLabel('site', { form: 'plural' });

  // Local UI state
  const [searchInput, setSearchInput] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterSite, setFilterSite] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  // Debounce search to avoid hammering the API on every keystroke
  const debouncedSearch = useDebounce(searchInput, 300);

  // Build server-side filter params
  const queryParams: any = { limit: 20 };
  if (debouncedSearch) queryParams.search = debouncedSearch;
  if (filterStatus !== 'all') queryParams.status = filterStatus;
  if (filterSite !== 'all') queryParams.site_id = filterSite;

  // React Query hooks
  const {
    data: assetsFlat,
    total: assetsTotal,
    loading: assetsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteAssets(queryParams);
  const { data: assetTypesData, isLoading: typesLoading } = useAssetTypes();
  const { data: sitesData } = useSites({ limit: 100 });
  const deleteAssetMutation = useDeleteAsset();
  const generateQRMutation = useGenerateAssetQR();
  const archiveAssetMutation = useArchiveAsset();
  const unarchiveAssetMutation = useUnarchiveAsset();
  const prefetchAsset = usePrefetchAsset();

  // DryStuff infinite query returns flat accumulated pages. Dedupe by id:
  // offset pagination can repeat a record across pages when data shifts,
  // which otherwise yields duplicate React keys in the list below.
  const assets: Asset[] = dedupeById(assetsFlat || []);
  const totalAssets = assetsTotal || 0;
  const assetTypes = assetTypesData || [];
  const sites = Array.isArray(sitesData) ? sitesData : sitesData?.data || [];
  const isLoading = assetsLoading || typesLoading;

  // Create asset type lookup map
  const assetTypeMap = React.useMemo(() => {
    const typeMap: Record<string, string> = {};
    assetTypes.forEach((type) => {
      typeMap[type.id] = type.name;
    });
    return typeMap;
  }, [assetTypes]);

  // Helper function to get asset type name from ID
  const getAssetTypeName = (asset: Asset): string => {
    // First check if asset_type_obj is populated (from backend with relations)
    if (asset.asset_type_obj?.name) {
      return asset.asset_type_obj.name;
    }
    // Check if backend populated asset_type object (relationship)
    if ((asset as any).asset_type?.name) {
      return (asset as any).asset_type.name;
    }
    // Use lookup map with asset_type_id (primary path)
    if (asset.asset_type_id && assetTypeMap[asset.asset_type_id]) {
      return assetTypeMap[asset.asset_type_id];
    }
    // Legacy fallback: asset_type as string
    if (typeof asset.asset_type === 'string' && asset.asset_type) {
      return asset.asset_type;
    }
    return 'Unknown';
  };

  const handleDelete = (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    deleteAssetMutation.mutate(id);
  };

  const handleArchive = (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to archive "${name}"?`)) return;
    archiveAssetMutation.mutate(id);
  };

  const handleUnarchive = (id: string, name: string) => {
    if (!window.confirm(`Unarchive "${name}" and restore it to active status?`)) return;
    unarchiveAssetMutation.mutate(id);
  };

  const handleGenerateQR = (id: string) => {
    generateQRMutation.mutate(id).then((qrToken: any) => {
      navigate(`/assets/${id}/qr`, { state: { qrToken } });
    }).catch(() => {});
  };

  // Client-side filter for type (server handles search, status, site_id)
  const filteredAssets = (assets || []).filter((asset: Asset) => {
    const assetTypeName = getAssetTypeName(asset);
    const matchesType = filterType === 'all' || assetTypeName === filterType;
    return matchesType;
  });

  // Get unique asset type names for filter dropdown
  const uniqueAssetTypeNames = Array.from(new Set((assets || []).map((a: Asset) => getAssetTypeName(a))));

  const getStatusColor = (status: AssetStatus): 'success' | 'warning' | 'danger' | 'default' => {
    switch (status) {
      case 'active':
        return 'success';
      case 'maintenance':
        return 'warning';
      case 'retired':
      case 'archived':
        return 'danger';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <NextStepNudge forKey="asset" />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{labelPlural}</h1>
          <p className="text-gray-600 mt-1">
            Manage your organization's {labelLowerPlural} and equipment
          </p>
        </div>
        <div className="flex items-center gap-3">
          {(isAdmin(user?.role) || isManager(user?.role)) && (
            <Button variant="secondary" onClick={() => navigate('/asset-types')}>
              <FaTags className="mr-2" />
              Manage {labelTypePlural}
            </Button>
          )}
          <Button onClick={() => navigate('/assets/new')}>
            <FaPlus className="mr-2" />
            Add {labelSingular}
          </Button>
        </div>
      </div>

      {/* Search and Filters Bar */}
      <Card>
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search assets by name, type, serial number, or site..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* View Toggle */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'grid'
                  ? 'bg-blue-100 text-blue-600'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <FaTh size={18} />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'table'
                  ? 'bg-blue-100 text-blue-600'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <FaList size={18} />
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 rounded-lg transition-colors ${
                showFilters
                  ? 'bg-blue-100 text-blue-600'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <FaFilter size={18} />
            </button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as AssetStatus | 'all')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="maintenance">Maintenance</option>
                <option value="retired">Retired</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                {uniqueAssetTypeNames.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Site</label>
              <select
                value={filterSite}
                onChange={(e) => setFilterSite(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Sites</option>
                {(sites || []).filter((s: any) => s.site_type === 'site').map((site: any) => (
                  <option key={site.id} value={site.id}>
                    {site.name} ({site.code})
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <Button
                variant="secondary"
                onClick={() => {
                  setFilterStatus('all');
                  setFilterType('all');
                  setFilterSite('all');
                  setSearchInput('');
                }}
                fullWidth
              >
                Clear Filters
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Showing {filteredAssets.length} of {totalAssets} assets
          {hasNextPage && <span className="text-gray-400"> (scroll for more)</span>}
        </p>
      </div>

      {/* Assets Display */}
      {filteredAssets.length === 0 ? (
        searchInput || filterStatus !== 'all' || filterType !== 'all' ? (
          <Card>
            <div className="text-center py-12">
              <FaBoxes className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">No {labelLowerPlural} match</h3>
              <p className="mt-2 text-sm text-gray-600">Try adjusting your search or filters</p>
            </div>
          </Card>
        ) : (
          <EmptyState
            icon={<FaBoxes />}
            title="Add the things you inspect"
            description={`${labelPlural} are the specific items checked at each location — fryers, vehicles, beds, machines, rooms. Each ${labelLower} belongs to a location and an ${labelLower} type, and gets a QR code for in-the-field scanning.`}
            primaryAction={{
              label: `Add ${labelSingular}`,
              icon: <FaPlus />,
              onClick: () => navigate('/assets/new'),
            }}
            nextStep={{ label: `Need ${labelSitePlural.toLowerCase()} or ${labelTypePlural.toLowerCase()} first?`, to: '/locations' }}
          />
        )
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredAssets.map((asset) => (
            <Card
              key={asset.id}
              className="hover:shadow-xl transition-shadow cursor-pointer group h-full"
              onClick={() => navigate(`/assets/${asset.id}`)}
              onMouseEnter={() => prefetchAsset(asset.id)}
            >
              <div className="space-y-4 flex flex-col h-full">
                {/* Asset Icon/Image */}
                {(() => {
                  const typeName = getAssetTypeName(asset);
                  const colors = [
                    'from-blue-50 to-indigo-100 text-blue-600',
                    'from-green-50 to-emerald-100 text-green-600',
                    'from-purple-50 to-violet-100 text-purple-600',
                    'from-orange-50 to-amber-100 text-orange-600',
                    'from-red-50 to-rose-100 text-red-600',
                    'from-cyan-50 to-teal-100 text-cyan-600',
                    'from-pink-50 to-fuchsia-100 text-pink-600',
                  ];
                  const colorIdx = typeName.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % colors.length;
                  const initials = typeName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
                  return (
                    <div className={`aspect-video bg-linear-to-br ${colors[colorIdx]} rounded-lg flex items-center justify-center`}>
                      <span className="text-3xl font-bold">{initials}</span>
                    </div>
                  );
                })()}

                {/* Asset Info */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 truncate">{asset.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">{getAssetTypeName(asset)}</p>
                  {asset.serial_number && (
                    <p className="text-xs text-gray-500 mt-1">SN: {asset.serial_number}</p>
                  )}
                </div>

                {/* Site & Status */}
                {asset.site && (
                  <div className="flex items-center gap-1.5 text-xs text-indigo-600 bg-indigo-50 rounded-md px-2 py-1 w-fit">
                    <FaBuilding className="shrink-0" />
                    <span className="truncate">{asset.site.name}</span>
                  </div>
                )}
                <div className="flex items-center justify-between mt-auto">
                  <Badge variant={getStatusColor(asset.status)}>{asset.status}</Badge>
                  {asset.location && (
                    <span className="text-xs text-gray-500">{asset.location}</span>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  {asset.status !== 'archived' && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleGenerateQR(asset.id);
                        }}
                        className="flex-1 px-3 py-2 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center gap-2"
                      >
                        <FaQrcode />
                        QR
                      </button>
                      <AddToPrintQueueButton
                        target={{ target_kind: 'asset', asset_id: asset.id }}
                        label={asset.name}
                        className="flex-1 justify-center py-2 text-sm rounded-lg bg-indigo-50 border-indigo-200"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/assets/${asset.id}/edit`);
                        }}
                        className="flex-1 px-3 py-2 text-sm bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
                      >
                        <FaEdit />
                        Edit
                      </button>
                    </>
                  )}
                  {asset.status === 'archived' ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUnarchive(asset.id, asset.name);
                      }}
                      className="flex-1 px-3 py-2 text-sm bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors flex items-center justify-center gap-2"
                    >
                      <FaBoxOpen />
                      Unarchive
                    </button>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleArchive(asset.id, asset.name);
                      }}
                      className="flex-1 px-3 py-2 text-sm bg-yellow-50 text-yellow-600 rounded-lg hover:bg-yellow-100 transition-colors flex items-center justify-center gap-2"
                    >
                      <FaArchive />
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(asset.id, asset.name);
                    }}
                    className="flex-1 px-3 py-2 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Name</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Type</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                    Serial Number
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Site</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                    Location
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredAssets.map((asset) => (
                  <tr
                    key={asset.id}
                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/assets/${asset.id}`)}
                    onMouseEnter={() => prefetchAsset(asset.id)}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{asset.name}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{getAssetTypeName(asset)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {asset.serial_number || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={getStatusColor(asset.status)}>{asset.status}</Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-indigo-600">
                      {asset.site ? asset.site.name : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{asset.location || '-'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {asset.status !== 'archived' && (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleGenerateQR(asset.id);
                              }}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Generate QR Code"
                            >
                              <FaQrcode />
                            </button>
                            <AddToPrintQueueButton
                              target={{ target_kind: 'asset', asset_id: asset.id }}
                              label={asset.name}
                            />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/assets/${asset.id}/edit`);
                              }}
                              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <FaEdit />
                            </button>
                          </>
                        )}
                        {asset.status === 'archived' ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUnarchive(asset.id, asset.name);
                            }}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Unarchive"
                          >
                            <FaBoxOpen />
                          </button>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleArchive(asset.id, asset.name);
                            }}
                            className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                            title="Archive"
                          >
                            <FaArchive />
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(asset.id, asset.name);
                          }}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      {/* Load More */}
      {hasNextPage && (
        <div className="flex justify-center pt-4">
          <Button
            variant="secondary"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? (
              <>
                <Spinner size="sm" />
                <span className="ml-2">Loading...</span>
              </>
            ) : (
              <>
                <FaChevronDown className="mr-2" />
                Load More Assets
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default Assets;
